import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';

export interface TVLSnapshot {
  snapshot_date: string;
  total_tvl: number;
  pool_count: number;
  created_at: string;
}

export type TimeRange = 'daily' | 'monthly';

/**
 * Hook to fetch historical TVL snapshots for charting
 */
export const useTVLHistory = (timeRange: TimeRange = 'daily', days: number = 30) => {
  const [snapshots, setSnapshots] = useState<TVLSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTVLHistory = async () => {
      try {
        setLoading(true);
        setError(null);

        // Calculate date range
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);

        let query = supabase
          .from('tvl_snapshots')
          .select('snapshot_date, total_tvl, pool_count, created_at')
          .eq('network', 'ink')
          .gte('snapshot_date', startDate.toISOString().split('T')[0])
          .lte('snapshot_date', endDate.toISOString().split('T')[0])
          .order('snapshot_date', { ascending: true });

        const { data, error: queryError } = await query;

        if (queryError) {
          throw new Error(`Failed to fetch TVL history: ${queryError.message}`);
        }

        if (!data || data.length === 0) {
          setSnapshots([]);
          setLoading(false);
          return;
        }

        // If monthly view, aggregate by month
        if (timeRange === 'monthly') {
          const monthlyData = new Map<string, { total_tvl: number; pool_count: number; dates: string[] }>();
          
          data.forEach((snapshot: TVLSnapshot) => {
            const date = new Date(snapshot.snapshot_date);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            
            if (!monthlyData.has(monthKey)) {
              monthlyData.set(monthKey, { total_tvl: 0, pool_count: 0, dates: [] });
            }
            
            const monthData = monthlyData.get(monthKey)!;
            // Use the last snapshot of the month for TVL
            monthData.total_tvl = snapshot.total_tvl;
            monthData.pool_count = snapshot.pool_count;
            monthData.dates.push(snapshot.snapshot_date);
          });
          
          // Convert to array format
          const monthlySnapshots: TVLSnapshot[] = Array.from(monthlyData.entries()).map(([monthKey, data]) => {
            // Use the last date of the month
            const lastDate = data.dates.sort()[data.dates.length - 1];
            return {
              snapshot_date: `${monthKey}-01`, // First day of month for display
              total_tvl: data.total_tvl,
              pool_count: data.pool_count,
              created_at: lastDate
            };
          });
          
          setSnapshots(monthlySnapshots);
        } else {
          // Daily view - return as-is
          setSnapshots(data as TVLSnapshot[]);
        }

        setLoading(false);
      } catch (err) {
        console.error('[useTVLHistory] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch TVL history');
        setLoading(false);
      }
    };

    fetchTVLHistory();
  }, [timeRange, days]);

  return {
    snapshots,
    loading,
    error,
  };
};

